import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { AuthShell, AuthIcon } from '../../components/layout/AuthShell';
import { Eye, EyeOff, UserPlus, Mail, ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FIELDS = [
  { id: 'name', label: 'Dein Name *', type: 'text', placeholder: 'Max Mustermann', required: true, testId: 'register-name' },
  { id: 'company_name', label: 'Name deiner Gastronomie *', type: 'text', placeholder: 'Restaurant Beispiel', required: true, testId: 'register-company' },
  { id: 'email', label: 'E-Mail-Adresse *', type: 'email', placeholder: 'ihre@email.de', required: true, testId: 'register-email' },
  { id: 'phone', label: 'Telefonnummer (optional)', type: 'tel', placeholder: '+49 123 456789', required: false, testId: 'register-phone' },
];

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('form'); // 'form', 'verify'
  const [loading, setLoading] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [resending, setResending] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    if (formData.password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    setLoading(true);

    try {
      const response = await register({
        name: formData.name,
        company_name: formData.company_name,
        email: formData.email,
        phone: formData.phone || null,
        password: formData.password
      });

      if (response?.status === 'pending_approval') {
        navigate('/registrierung-geprueft');
      } else {
        setStep('verify');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/verify-email-code`, {
        email: formData.email,
        code: verificationCode
      });

      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      window.location.href = '/freischaltung-ausstehend';
    } catch (err) {
      setError(err.response?.data?.detail || 'Ungültiger Code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    setError('');
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: formData.email });
      alert('Ein neuer Code wurde an deine E-Mail-Adresse gesendet.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setResending(false);
    }
  };

  if (step === 'verify') {
    return (
      <AuthShell
        testId="register-verify"
        eyebrow="Schritt 2 von 2"
        title={<>E-Mail <span className="serif-it text-purple">bestätigen.</span></>}
        intro={<>Wir haben einen 6-stelligen Code an <strong className="text-ink">{formData.email}</strong> gesendet.</>}
      >
        <AuthIcon><Mail className="w-8 h-8" /></AuthIcon>

        <form onSubmit={handleVerifyCode} className="space-y-4">
          <div>
            <Label htmlFor="code" className="mb-2 block">Bestätigungscode</Label>
            <Input
              id="code"
              type="text"
              inputMode="numeric"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              className="h-14 text-center text-2xl tracking-[0.4em] font-display font-bold"
              maxLength={6}
              data-testid="verify-code-input"
            />
          </div>

          {error && (
            <p className="text-red-700 font-medium" data-testid="verify-code-error">{error}</p>
          )}

          <Button
            type="submit"
            disabled={loading || verificationCode.length !== 6}
            className="btn--block"
            data-testid="verify-code-submit"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Bestätigen'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-ink/60 mb-2">Keinen Code erhalten?</p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleResendCode}
            disabled={resending}
            className="text-purple"
            data-testid="verify-resend-btn"
          >
            {resending ? 'Wird gesendet...' : 'Code erneut senden'}
          </Button>
        </div>

        <div className="mt-4 pt-4 border-t border-ink/15">
          <button
            type="button"
            onClick={() => setStep('form')}
            className="flex items-center gap-2 text-ink/60 hover:text-ink mx-auto"
            data-testid="verify-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zur Registrierung
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      testId="register-page"
      eyebrow="Registrierung"
      title={<>Konto <span className="serif-it text-purple">erstellen.</span></>}
      intro="Starte jetzt mit deiner digitalen Gastro-Plattform"
      below={
        <Link to="/" className="text-ink/60 hover:text-ink" data-testid="register-back-home">
          ← Zurück zur Startseite
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {FIELDS.map((f) => (
          <div key={f.id}>
            <Label htmlFor={f.id} className="mb-2 block">{f.label}</Label>
            <Input
              id={f.id}
              name={f.id}
              type={f.type}
              value={formData[f.id]}
              onChange={handleChange}
              placeholder={f.placeholder}
              required={f.required}
              data-testid={f.testId}
            />
          </div>
        ))}

        <div>
          <Label htmlFor="password" className="mb-2 block">Passwort *</Label>
          <div className="relative">
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              placeholder="Mindestens 6 Zeichen"
              required
              className="pr-12"
              data-testid="register-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/60 hover:text-ink"
              aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
              data-testid="register-toggle-password"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <div>
          <Label htmlFor="confirmPassword" className="mb-2 block">Passwort bestätigen *</Label>
          <Input
            id="confirmPassword"
            name="confirmPassword"
            type={showPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder="Passwort wiederholen"
            required
            data-testid="register-confirm-password"
          />
        </div>

        {error && (
          <p className="text-red-700 font-medium" data-testid="register-error">{error}</p>
        )}

        <Button type="submit" disabled={loading} className="btn--block mt-2" data-testid="register-submit">
          {loading ? 'Registrieren...' : (<><UserPlus className="w-5 h-5" />Kostenlos registrieren</>)}
        </Button>
      </form>

      <p className="mt-6 text-center text-ink/80">
        Bereits ein Konto?{' '}
        <Link to="/login" className="text-purple font-bold hover:underline" data-testid="register-login-link">
          Jetzt anmelden
        </Link>
      </p>
    </AuthShell>
  );
};

export default RegisterPage;
